<script setup>
import { computed, ref } from 'vue';
import { Handle, Position } from '@vue-flow/core';
import { NodeToolbar } from '@vue-flow/node-toolbar';
import { useI18n } from 'vue-i18n';
import { triggerNodeAction } from '../nodeEventBus';

const props = defineProps({
  id: { type: String, default: '' },
  data: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
});

const { t } = useI18n();
const isHovered = ref(false);
let hoverTimeout = null;
const onMouseEnter = () => {
  clearTimeout(hoverTimeout);
  isHovered.value = true;
};
const onMouseLeave = () => {
  hoverTimeout = setTimeout(() => {
    isHovered.value = false;
  }, 300);
};

const items = computed(() => props.data?.content_items || []);
const label = computed(
  () => props.data?.label || t('FLOW_BUILDER.SIDEBAR.BLOCK')
);

const itemIcon = type => {
  const icons = {
    text: 'i-lucide-type',
    image: 'i-lucide-image',
    audio: 'i-lucide-music',
    video: 'i-lucide-video',
    file: 'i-lucide-paperclip',
    delay: 'i-lucide-timer',
  };
  return icons[type] || 'i-lucide-square';
};
</script>

<template>
  <div
    class="flow-node min-w-[200px] max-w-[260px] min-h-[80px] bg-white dark:bg-n-slate-1 rounded-[10px] p-2 text-[10px] shadow-md hover:shadow-lg border transition-all relative"
    :class="
      selected
        ? 'border border-green-500 shadow-lg'
        : 'border border-transparent hover:border-n-blue-8'
    "
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <NodeToolbar :is-visible="isHovered" :position="Position.Top" :offset="8">
      <div
        class="flex gap-1 bg-white dark:bg-n-slate-2 rounded-lg shadow-lg p-1"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
      >
        <button
          class="w-8 h-8 rounded-md flex items-center justify-center hover:bg-n-blue-1 dark:hover:bg-n-blue-2 transition-colors"
          :title="t('FLOW_BUILDER.SIDEBAR.DUPLICATE')"
          @click.stop="triggerNodeAction('duplicate', props.id)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2563eb"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
            <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
          </svg>
        </button>
        <button
          class="w-8 h-8 rounded-md flex items-center justify-center hover:bg-n-ruby-1 dark:hover:bg-n-ruby-2 transition-colors"
          :title="t('FLOW_BUILDER.SIDEBAR.DELETE')"
          @click.stop="triggerNodeAction('delete', props.id)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#dc2626"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M3 6h18" />
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
          </svg>
        </button>
      </div>
    </NodeToolbar>
    <div class="flex items-center gap-2 mb-1">
      <span class="i-lucide-layout-grid text-[10px] text-n-indigo-10" />
      <span class="font-semibold text-n-slate-12 text-[9px] truncate">
        {{ label }}
      </span>
    </div>
    <div v-if="items.length === 0" class="text-[8px] text-n-slate-11 py-1">
      {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.EMPTY') }}
    </div>
    <div v-else class="flex flex-col gap-1">
      <template v-for="(item, index) in items" :key="index">
        <!-- Image thumbnail -->
        <div
          v-if="item.type === 'image' && item.url"
          class="relative rounded-md overflow-hidden bg-n-slate-2 dark:bg-n-slate-3"
        >
          <img
            :src="item.url"
            :alt="item.caption || item.file_name || ''"
            class="w-full max-h-20 object-cover"
          />
          <p
            v-if="item.caption"
            class="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 text-[9px] text-white bg-black/50 truncate"
          >
            {{ item.caption }}
          </p>
        </div>
        <!-- Other items: icon + text -->
        <div v-else class="flex items-start gap-1.5 text-[8px] text-n-slate-11">
          <span
            :class="itemIcon(item.type)"
            class="text-[8px] shrink-0 mt-0.5"
          />
          <span class="line-clamp-2 break-words">
            {{
              item.type === 'text'
                ? item.content || '...'
                : item.type === 'delay'
                  ? `${item.delay_value || 5} ${item.delay_unit || 'seconds'}`
                  : item.file_name ||
                    t(
                      `FLOW_BUILDER.NODE_EDITOR.BLOCK.TYPES.${item.type.toUpperCase()}`
                    )
            }}
          </span>
        </div>
      </template>
    </div>
    <div
      v-if="props.data?.wait_for_response"
      class="mt-1.5 flex flex-col gap-1 px-1.5 py-1 rounded bg-n-slate-1 dark:bg-n-slate-2"
    >
      <div class="flex items-center gap-1 text-[7px] text-n-slate-11">
        <span class="i-lucide-message-circle-reply text-[8px]" />
        {{ props.data?.attribute_display_name || props.data?.attribute_key }}
      </div>
      <div
        v-if="props.data?.timeout_value"
        class="flex items-center gap-1 text-[7px] text-n-slate-11"
      >
        <span class="i-lucide-timer text-[8px]" />
        {{ props.data.timeout_value }}
        {{
          t(
            `FLOW_BUILDER.NODE_EDITOR.BLOCK.DELAY_UNITS.${(props.data?.timeout_unit || 'minutes').toUpperCase()}`
          )
        }}
      </div>
    </div>
    <Handle
      type="target"
      :position="Position.Left"
      class="!w-1.5 !h-1.5 !border !border-white"
      :title="t('FLOW_BUILDER.NODE_EDITOR.HANDLE_INPUT')"
    />
    <div
      v-if="props.data?.wait_for_response"
      class="flex justify-between w-full mt-2 px-1 pb-1"
    >
      <span
        class="text-[5px] text-n-amber-9 font-medium text-left flex-1 flex items-center gap-0.5"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="8"
          height="8"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.OUTPUT_RESPONSE') }}
      </span>
      <span
        class="text-[5px] text-n-ruby-9 font-medium text-center flex-1 flex items-center gap-0.5"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="8"
          height="8"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
        {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.OUTPUT_TIMEOUT') }}
      </span>
      <span
        class="text-[5px] text-n-blue-9 font-medium text-right flex-1 flex items-center gap-0.5"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="8"
          height="8"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
        {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.OUTPUT_DEFAULT') }}
      </span>
    </div>
    <div v-else class="flex justify-end w-full mt-2 px-1 pb-1">
      <span
        class="text-[5px] text-n-blue-9 font-medium text-right flex items-center gap-0.5"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="8"
          height="8"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
        {{ t('FLOW_BUILDER.NODE_EDITOR.BLOCK.OUTPUT_DEFAULT') }}
      </span>
    </div>
    <Handle
      v-if="props.data?.wait_for_response"
      id="response"
      type="source"
      :position="Position.Right"
      class="!bg-n-amber-9 !w-1.5 !h-1.5 !border !border-white !top-[20%]"
      :title="t('FLOW_BUILDER.NODE_EDITOR.BLOCK.OUTPUT_RESPONSE')"
    />
    <Handle
      v-if="props.data?.wait_for_response"
      id="timeout"
      type="source"
      :position="Position.Right"
      class="!bg-n-ruby-9 !w-1.5 !h-1.5 !border !border-white"
      :title="t('FLOW_BUILDER.NODE_EDITOR.BLOCK.OUTPUT_TIMEOUT')"
    />
    <Handle
      id="default"
      type="source"
      :position="Position.Right"
      :class="
        props.data?.wait_for_response
          ? '!bg-n-blue-9 !w-1.5 !h-1.5 !border !border-white !top-[80%]'
          : '!bg-n-blue-9 !w-1.5 !h-1.5 !border !border-white'
      "
      :title="t('FLOW_BUILDER.NODE_EDITOR.BLOCK.OUTPUT_DEFAULT')"
    />
  </div>
</template>
